import { RelativeTime } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { Button } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { useSupportReporters } from '../api'

const HEAD = 'px-2 py-1.5 text-2xs font-normal tracking-wide text-text-3 uppercase'

export interface ReportersListProps {
  /** Pasa a la lista de reportes de esa persona. */
  onSelect: (email: string) => void
}

/**
 * El panel por usuario: quién reporta, desde qué apps y cómo le fue.
 *
 * Primero quien tiene más abiertos —es a quien hay que mirar—; el orden lo
 * decide el servidor. "Resueltos por la IA" son los que el asistente le
 * resolvió con el manual de la app: cuentan aparte para ver cuánto alcanza.
 */
export function ReportersList({ onSelect }: ReportersListProps) {
  const t = useT()
  const query = useSupportReporters()

  if (query.isPending) return <SkeletonRows rows={5} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const filas = query.data
  if (filas.length === 0) {
    return (
      <EmptyState
        title={t('support.users.empty.title')}
        description={t('support.users.empty.description')}
      />
    )
  }

  return (
    <div className="hairline overflow-x-auto rounded-[var(--radius-card)] bg-surface-1">
      <table className="w-full min-w-[720px]" aria-label={t('support.users.listLabel')}>
        <thead>
          <tr className="hairline-b">
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('support.users.column.user')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('support.users.column.apps')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-right')}>
              {t('support.users.column.open')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-right')}>
              {t('support.users.column.resolvedByAi')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-right')}>
              {t('support.users.column.done')}
            </th>
            <th scope="col" className={cn(HEAD, 'text-left')}>
              {t('support.users.column.last')}
            </th>
            <th scope="col" className={HEAD}>
              <span className="sr-only">{t('support.users.seeTickets')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {filas.map((fila) => (
            <tr
              key={fila.email ?? fila.name ?? ''}
              className="hairline-b hover:bg-surface-2/60"
              style={{ height: 'var(--row-h)' }}
            >
              <td className="px-2">
                <div className="flex flex-col">
                  <span className="truncate text-xs text-text-1">
                    {fila.name ?? fila.email ?? t('support.users.anonymous')}
                  </span>
                  {fila.name && fila.email ? (
                    <span className="truncate text-2xs text-text-3">{fila.email}</span>
                  ) : null}
                </div>
              </td>
              <td className="px-2 text-xs text-text-2">{fila.apps.join(', ')}</td>
              <td className="num px-2 text-right text-xs text-text-1">{fila.open}</td>
              <td className="num px-2 text-right text-xs text-text-2">{fila.resolvedByAi}</td>
              <td className="num px-2 text-right text-xs text-text-2">{fila.done}</td>
              <td className="px-2">
                <RelativeTime value={fila.lastReportedAt} className="text-2xs" />
              </td>
              <td className="px-2 text-right">
                {fila.email ? (
                  <Button
                    variant="ghost"
                    aria-label={t('support.users.seeTicketsOf', {
                      quien: fila.name ?? fila.email,
                    })}
                    onClick={() => onSelect(fila.email!)}
                  >
                    {t('support.users.seeTickets')}
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
